import React, { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { SegmentedControl } from '@components/primitives/SegmentedControl';
import {
  type ExpandableFieldItem,
  type SchedulingData,
  useAppointmentTypes,
} from '@features/appointment-types';
import {
  type Appointment as AppointmentResponse,
  AppointmentPropertiesTable,
  CustomRecurrenceModal,
  type SeriesResponse,
  useAppointmentFormReset,
  useAppointmentFormValidation,
  useAppointmentSubmission,
  useDurationSchedule,
  useFormsAndReminders,
  useOrgScheduleAccess,
  useOrgScheduleFilters,
  useRecurrence,
  useTelehealth,
} from '@features/appointments';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import { useOrganizationId, useTemplatesList } from '@features/user';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import {
  getModalityPlaceOfServiceError,
  getTelehealthForModality,
  normalizeCptCodes,
  type ConcreteVisitModality,
  type VisitModality,
} from '@utils/modalityPlaceOfService';

import { type Patient } from '../../../types/types';
import type { BlockedTimeOccurrence } from '../types/blockedTime';
import { appointmentOverridesTypeDefaults, resolveTypeSessionDefaults } from '../utils/typeDefaults';

import BlockedTimeForm, {
  type BlockedTimeActionState,
  type BlockedTimeFormHandle,
} from './BlockedTimeForm';

type CalendarCreateMode = 'appointment' | 'blocked_time';

const CREATE_MODE_OPTIONS = [
  { label: 'Appointment', value: 'appointment' },
  { label: 'Out of office', value: 'blocked_time' },
];

const isCalendarCreateMode = (value: string): value is CalendarCreateMode =>
  value === 'appointment' || value === 'blocked_time';

interface AddAppointmentModalProps {
  open: boolean;
  onClose: () => void;
  initialMode?: CalendarCreateMode;
  initialScheduledTime?: Date;
  initialDuration?: number;
  initialProviderId?: string;
  initialAllDay?: boolean;
  initialPatient?: Patient;
  blockedTime?: BlockedTimeOccurrence | null;
  existingAppointments?: AppointmentResponse[];
  onAppointmentCreated?: (appointment: AppointmentResponse | SeriesResponse) => void;
  onBlockedTimeSaved?: () => void;
  onBlockedTimeDeleted?: () => void;
}

const AddAppointmentModal: React.FC<AddAppointmentModalProps> = ({
  open,
  onClose,
  initialMode = 'appointment',
  initialScheduledTime,
  initialDuration,
  initialProviderId,
  initialAllDay = false,
  initialPatient,
  blockedTime,
  existingAppointments = [],
  onAppointmentCreated,
  onBlockedTimeSaved,
  onBlockedTimeDeleted,
}) => {
  const templatesList = useTemplatesList();
  const [enterKeyReleased, setEnterKeyReleased] = useState(true);
  const { appointment, blockedTime: blockedTimeTracking } = useEventTracking();
  const [activeMode, setActiveMode] = useState<CalendarCreateMode>(initialMode);
  const trackedModalOpenRef = useRef(false);
  const blockedTimeFormRef = useRef<BlockedTimeFormHandle>(null);
  const [blockedTimeActionState, setBlockedTimeActionState] = useState<BlockedTimeActionState>({
    isEditing: false,
    isSubmitting: false,
  });

  // Org schedule admin state
  const { isOrgViewActive } = useOrgScheduleFilters();
  const { currentUserId, hasOrgScheduleAccess, isCurrentUserClinician } = useOrgScheduleAccess();
  const isOrgScheduleActive = hasOrgScheduleAccess && isOrgViewActive;
  // Non-clinicians (e.g. practice admins) are not valid providers — never
  // default the appointment to them; they must pick a provider (GDN-2225).
  const defaultProviderId = isCurrentUserClinician ? currentUserId : undefined;
  const [selectedProviderId, setSelectedProviderId] = useState<string | undefined>(
    isOrgScheduleActive ? (initialProviderId ?? defaultProviderId) : undefined,
  );

  // Patient state
  const [selectedPatient, setSelectedPatient] = useState<Patient | undefined>(initialPatient);

  // Notes state
  const [notes, setNotes] = useState('');

  // Default labels (org-scoped) — pre-filled from the appointment type's defaults.
  const organizationId = useOrganizationId();
  const [labelIds, setLabelIds] = useState<string[]>([]);

  // Whether the current type was auto-applied (provider's most-used) rather than
  // user-picked. Drives the "Suggested" label; cleared on an explicit pick. (GDN-2228)
  const [isTypeSuggested, setIsTypeSuggested] = useState(false);

  // Track source for analytics
  const [modalOpenSource, setModalOpenSource] = useState<string>('unknown');

  // Get appointment types for auto-selection (use selected provider in org mode)
  const { appointmentTypes } = useAppointmentTypes(
    undefined,
    isOrgScheduleActive ? selectedProviderId : undefined,
  );

  // Use modular hooks
  const durationSchedule = useDurationSchedule({
    initialDate: initialScheduledTime,
    initialDuration: initialDuration,
  });

  const recurrence = useRecurrence();

  const telehealth = useTelehealth();
  const [modality, setModality] = useState<VisitModality>(null);
  const [placeOfService, setPlaceOfService] = useState<string | null>(null);
  const [cptCodes, setCptCodes] = useState<string[]>([]);
  const [useJotmeet, setUseJotmeet] = useState(false);

  const formsAndReminders = useFormsAndReminders();
  const selectedTypeId = formsAndReminders.selectedAppointmentType?.encounter_type_id;

  // Whether the current values deviate from the selected type's defaults.
  // Persisted as override_defaults on save so the appointment stops following
  // its type (billing place-of-service resolution depends on this flag).
  const overridesTypeDefaults = appointmentOverridesTypeDefaults({
    appointmentType: formsAndReminders.selectedAppointmentType,
    duration: durationSchedule.selectedDuration,
    modality,
    placeOfService,
    cptCodes,
    useJotmeet,
    forms: formsAndReminders.selectedForms,
    reminders: formsAndReminders.selectedReminders,
  });

  // Form validation. The backend assigns omitted provider_id to the caller,
  // so an org-view create must carry an explicit provider (GDN-2225).
  const validation = useAppointmentFormValidation({
    selectedPatient,
    selectedAppointmentType: formsAndReminders.selectedAppointmentType,
    durationSchedule,
    mode: 'create',
    requireProvider: isOrgScheduleActive,
    providerId: selectedProviderId,
  });

  // Form submission
  const submission = useAppointmentSubmission({
    mode: 'create',
    selectedPatient,
    selectedAppointmentType: formsAndReminders.selectedAppointmentType,
    durationSchedule,
    recurrence,
    templateId: templatesList?.[0]?.template_id || null,
    selectedForms: formsAndReminders.selectedForms,
    selectedReminders: formsAndReminders.selectedReminders,
    telehealth: telehealth.telehealth,
    modality,
    placeOfService,
    cptCodes,
    useJotmeet,
    notes: notes || undefined,
    overridesTypeDefaults,
    providerId: isOrgScheduleActive ? selectedProviderId : undefined,
    onAppointmentCreated,
    labelIds,
  });

  // Form reset
  const { resetForm: resetFormData } = useAppointmentFormReset({
    durationSchedule,
    recurrence,
    onPatientReset: () => setSelectedPatient(undefined),
  });

  const handleTelehealthChange = (checked: boolean) => {
    telehealth.handleTelehealthChange(checked);
    setModality(checked ? 'video' : 'in_person');
    if (!checked) {
      setUseJotmeet(false);
    }
  };

  const handleModalityChange = (nextModality: ConcreteVisitModality | null) => {
    setModality(nextModality);
    telehealth.setTelehealth(getTelehealthForModality(nextModality));
    if (nextModality !== 'video') {
      setUseJotmeet(false);
    }
  };

  const handleCptCodesChange = (nextCptCodes: string[]) => {
    setCptCodes(normalizeCptCodes(nextCptCodes));
  };

  // Set initial patient when it changes
  useEffect(() => {
    if (initialPatient) {
      setSelectedPatient(initialPatient);
    }
  }, [initialPatient]);

  useEffect(() => {
    if (open && isOrgScheduleActive) {
      setSelectedProviderId(initialProviderId ?? defaultProviderId);
    }
  }, [open, isOrgScheduleActive, initialProviderId, defaultProviderId]);

  useEffect(() => {
    if (open) {
      setActiveMode(blockedTime ? 'blocked_time' : initialMode);
    }
  }, [blockedTime, initialMode, open]);

  // Track modal opening
  useEffect(() => {
    if (!open) {
      trackedModalOpenRef.current = false;
      return;
    }
    if (trackedModalOpenRef.current) return;

    const source = initialPatient ? 'patient' : initialScheduledTime ? 'calendar' : 'quick_action';
    const openedMode = blockedTime ? 'blocked_time' : initialMode;
    setModalOpenSource(source);

    if (openedMode === 'blocked_time') {
      blockedTimeTracking.modalOpened({
        mode: blockedTime ? 'edit' : 'create',
        source: blockedTime ? 'event' : 'calendar',
        is_org_schedule: isOrgScheduleActive,
      });
    } else {
      appointment.modalOpened({ source, is_org_schedule: isOrgScheduleActive });
    }

    trackedModalOpenRef.current = true;
    // Snapshot modal-open metadata once per open; mode switches are tracked separately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Initialize date and time when modal opens
  useEffect(() => {
    if (open && !initialScheduledTime && !durationSchedule.date) {
      // Set smart defaults for date and time if not provided
      const now = new Date();

      // Calculate next 15-minute interval from now
      const currentMinutes = now.getMinutes();
      const roundedMinutes = Math.ceil(currentMinutes / 15) * 15;

      // Create the next appointment time
      const nextAppointmentTime = new Date(now);
      nextAppointmentTime.setMinutes(roundedMinutes, 0, 0); // Set seconds and milliseconds to 0

      // If rounding put us in the next hour, the Date object handles this automatically
      // If we're already at the end of the day, it will roll to the next day

      // Set date (could be today or tomorrow if we rolled over)
      const dateString = nextAppointmentTime.toISOString().split('T')[0];
      const timeString = nextAppointmentTime.toTimeString().substring(0, 5); // HH:mm format

      durationSchedule.setDate(dateString);
      durationSchedule.setStartTime(timeString);
    }
  }, [open, initialScheduledTime, durationSchedule]);

  // Reset all state when modal closes
  useEffect(() => {
    if (!open) {
      const timeoutId = setTimeout(() => {
        resetForm();
      }, 300);
      return () => clearTimeout(timeoutId);
    } else {
      // When modal opens, ensure patient is set from props
      if (initialPatient && !selectedPatient) {
        setSelectedPatient(initialPatient);
      }
    }
  }, [open, initialPatient]);

  // Apply the selected type's defaults whenever the type changes, and re-apply
  // them when the modal reopens (the component stays mounted across opens:
  // resetForm clears the fields but keeps the last-picked type). A
  // calendar-dragged duration (initialDuration) wins over the first
  // application after open; explicitly picking a type afterwards applies that
  // type's duration.
  const initialDurationAppliedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      initialDurationAppliedRef.current = false;
      return;
    }
    const appointmentType = formsAndReminders.selectedAppointmentType;
    if (!appointmentType) return;

    const preserveDraggedDuration = Boolean(initialDuration) && !initialDurationAppliedRef.current;
    initialDurationAppliedRef.current = true;
    if (!preserveDraggedDuration && appointmentType.default_duration) {
      durationSchedule.handleDurationSelect(appointmentType.default_duration);
    }

    formsAndReminders.populateFormsFromDefaults(appointmentType);
    formsAndReminders.populateRemindersFromDefaults(appointmentType);
    setLabelIds(appointmentType.default_label_ids ?? []);

    const sessionDefaults = resolveTypeSessionDefaults(appointmentType);
    setModality(sessionDefaults.modality);
    telehealth.setTelehealth(sessionDefaults.telehealth);
    setPlaceOfService(sessionDefaults.placeOfService);
    setCptCodes(sessionDefaults.cptCodes);
    setUseJotmeet(sessionDefaults.useJotmeet);
    // Keyed on the type id (not the record) so list refetches and field edits
    // never re-apply defaults over in-progress user input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedTypeId]);

  // Suggest the provider's most-used appointment type when types load, or
  // re-suggest if the current type is stale. The list arrives usage-sorted from
  // the server (usage_count DESC, name ASC — JOT-1023), so [0] is the provider's
  // most-used, not an arbitrary first row. Labeled "Suggested" until the user
  // makes an explicit pick. (GDN-2228)
  useEffect(() => {
    if (!appointmentTypes || appointmentTypes.length === 0) return;

    const currentType = formsAndReminders.selectedAppointmentType;

    // No type selected → suggest the most-used
    if (!currentType) {
      setIsTypeSuggested(true);
      formsAndReminders.handleAppointmentTypeSelect(appointmentTypes[0]);
      return;
    }

    // Current type not in the new list (e.g. provider switched) → suggest from new list
    const stillValid = appointmentTypes.some(
      t => t.encounter_type_id === currentType.encounter_type_id,
    );
    if (!stillValid) {
      setIsTypeSuggested(true);
      formsAndReminders.handleAppointmentTypeSelect(appointmentTypes[0]);
    }
  }, [appointmentTypes, formsAndReminders.selectedAppointmentType]);

  // Handle creating a new encounter
  const handleCreateEncounter = async () => {
    const sessionError = getModalityPlaceOfServiceError({
      modality,
      placeOfService,
    });
    if (sessionError) {
      submission.setError(sessionError);
      return;
    }

    if (!validation.isFormValid) {
      return;
    }

    const startTime = Date.now();
    await submission.submit();

    // Reset form and close modal if successful
    if (!submission.error) {
      // Track successful creation
      appointment.create({
        appointment_type_id: formsAndReminders.selectedAppointmentType?.encounter_type_id,
        duration_minutes: durationSchedule.selectedDuration,
        is_recurring: recurrence.recurrence !== 'none',
        recurrence_pattern: recurrence.recurrence !== 'none' ? recurrence.recurrence : undefined,
        is_telehealth: telehealth.telehealth,
        has_forms: formsAndReminders.selectedForms && formsAndReminders.selectedForms.length > 0,
        forms_count: formsAndReminders.selectedForms?.length || 0,
        has_reminders:
          formsAndReminders.selectedReminders && formsAndReminders.selectedReminders.length > 0,
        reminders_count: formsAndReminders.selectedReminders?.length || 0,
        source: modalOpenSource,
        is_org_schedule: isOrgScheduleActive,
        target_provider_id: isOrgScheduleActive ? selectedProviderId : undefined,
      });

      // Track note addition if notes were included
      if (notes.trim()) {
        appointment.noteAdded({
          appointment_id: 'new',
          note_length: notes.trim().length,
          is_org_schedule: isOrgScheduleActive,
          target_provider_id: isOrgScheduleActive ? selectedProviderId : undefined,
        });
      }

      resetForm();
      onClose();
    } else {
      // Track creation failure
      appointment.creationFailed({
        error_type: 'submission_error',
        error_message: submission.error,
        is_org_schedule: isOrgScheduleActive,
      });
    }
  };

  // Reset the form and all modal state
  const resetForm = () => {
    resetFormData();
    setSelectedPatient(undefined);
    setNotes('');
    setLabelIds([]);
    setModality(null);
    setPlaceOfService(null);
    setCptCodes([]);
    setUseJotmeet(false);
    setSelectedProviderId(isOrgScheduleActive ? (initialProviderId ?? defaultProviderId) : undefined);
  };

  // Handle closing the modal
  const handleClose = () => {
    if (activeMode === 'appointment') {
      // Track abandonment if closing without creating
      appointment.creationAbandoned({
        source: modalOpenSource,
        had_patient_selected: !!selectedPatient,
        had_type_selected: !!formsAndReminders.selectedAppointmentType,
        is_org_schedule: isOrgScheduleActive,
      });
    }

    resetForm();
    onClose();
  };

  const handleModeChange = (nextMode: string) => {
    if (!isCalendarCreateMode(nextMode) || nextMode === activeMode) return;
    setActiveMode(nextMode);
    blockedTimeTracking.modeSelected({ mode: nextMode });
  };

  // Handle provider selection (org schedule admin)
  const handleProviderSelect = (providerId: string) => {
    setSelectedProviderId(providerId);
    // Reset appointment type since types are per-provider
    formsAndReminders.handleAppointmentTypeSelect(undefined);
  };

  // Handle patient selection
  const handlePatientSelect = (patient: Patient | undefined) => {
    setSelectedPatient(patient);
  };

  // Wrap appointment type selection to add tracking
  const handleAppointmentTypeSelect = (appointmentType: any) => {
    formsAndReminders.handleAppointmentTypeSelect(appointmentType);
    if (appointmentType) {
      appointment.typeSelected({
        appointment_type_id: appointmentType.encounter_type_id,
        color_enum: appointmentType.color,
        position_index: appointmentTypes?.findIndex(
          t => t.encounter_type_id === appointmentType.encounter_type_id,
        ),
        total_count: appointmentTypes?.length,
        replaced_suggestion: isTypeSuggested,
        surface: 'add_appointment_modal',
      });
    }
    setIsTypeSuggested(false);
  };

  // Wrap recurrence change to track when recurring is enabled
  const handleRecurrenceChange = (value: string) => {
    recurrence.handleRecurrenceChange(value);
    if (value !== 'none') {
      appointment.recurringEnabled({ recurrence_pattern: value });
    }
  };

  const toSchedulingData = (item: ExpandableFieldItem): SchedulingData => ({
    name: item.name,
    timing: item.timing ?? '',
    customTiming: item.offset ?? '',
    formId: item.formId,
  });

  const handleFormsChange = (nextForms: ExpandableFieldItem[]) => {
    for (const currentForm of formsAndReminders.selectedForms) {
      if (!nextForms.some(nextForm => nextForm.id === currentForm.id)) {
        formsAndReminders.handleRemoveForm(currentForm.id);
      }
    }

    for (const nextForm of nextForms) {
      const existingForm = formsAndReminders.selectedForms.find(form => form.id === nextForm.id);
      if (existingForm) {
        formsAndReminders.handleUpdateForm(nextForm.id, toSchedulingData(nextForm));
      } else {
        formsAndReminders.handleAddForm(toSchedulingData(nextForm));
      }
    }
  };

  const handleRemindersChange = (nextReminders: ExpandableFieldItem[]) => {
    for (const currentReminder of formsAndReminders.selectedReminders) {
      if (!nextReminders.some(nextReminder => nextReminder.id === currentReminder.id)) {
        formsAndReminders.handleRemoveReminder(currentReminder.id);
      }
    }

    for (const nextReminder of nextReminders) {
      const existingReminder = formsAndReminders.selectedReminders.find(
        reminder => reminder.id === nextReminder.id,
      );
      if (existingReminder) {
        formsAndReminders.handleUpdateReminder(nextReminder.id, toSchedulingData(nextReminder));
      } else {
        formsAndReminders.handleAddReminder(toSchedulingData(nextReminder));
      }
    }
  };

  // Handle Enter key press and release to submit form
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (activeMode !== 'appointment') return;

    if (event.key === 'Enter') {
      if (
        enterKeyReleased &&
        !event.shiftKey &&
        !event.ctrlKey &&
        validation.isFormValid &&
        !submission.isSubmitting
      ) {
        event.preventDefault();
        handleCreateEncounter();
      }
      setEnterKeyReleased(false);
    }
  };

  const handleKeyUp = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      setEnterKeyReleased(true);
    }
  };

  const sessionError = getModalityPlaceOfServiceError({
    modality,
    placeOfService,
  });

  // Don't render anything if modal is not open to prevent initialization issues
  if (!open) {
    return null;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        scroll="paper"
        sx={{
          '& .MuiDialog-paper': {
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: ELIG_TOKENS.bg,
            color: ELIG_TOKENS.ink,
          },
        }}
        maxWidth={'md'}
      >
        <DialogTitle
          sx={{
            m: 0,
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            borderBottom: `1px solid ${ELIG_TOKENS.line}`,
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                color: ELIG_TOKENS.midnight,
                fontWeight: 650,
                lineHeight: 1.2,
              }}
            >
              {activeMode === 'blocked_time'
                ? blockedTime
                  ? 'Edit Out of Office'
                  : 'Out of Office'
                : 'New Appointment'}
            </Typography>
            <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
              {activeMode === 'blocked_time' ? 'Unavailable time' : 'Properties'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" aria-label="Close appointment modal">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: activeMode === 'blocked_time' ? 0 : 3,
            pt: 0,
            pb: activeMode === 'blocked_time' ? 0 : 2.5,
          }}
        >
          <Stack
            spacing={activeMode === 'blocked_time' ? 0 : 2}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
          >
            {!blockedTime && (
              <Box
                sx={{
                  alignSelf: 'flex-start',
                  pt: 2,
                  ...(activeMode === 'blocked_time' ? { px: 3 } : {}),
                }}
              >
                <SegmentedControl
                  ariaLabel="Appointment creation mode"
                  options={CREATE_MODE_OPTIONS}
                  value={activeMode}
                  onChange={handleModeChange}
                  size="small"
                />
              </Box>
            )}

            {activeMode === 'blocked_time' ? (
              <BlockedTimeForm
                ref={blockedTimeFormRef}
                mode={blockedTime ? 'edit' : 'create'}
                blockedTime={blockedTime}
                initialScheduledTime={initialScheduledTime}
                initialDuration={initialDuration}
                initialProviderId={initialProviderId}
                initialAllDay={initialAllDay}
                isOrgScheduleActive={isOrgScheduleActive}
                defaultProviderId={defaultProviderId}
                appointments={existingAppointments}
                onClose={handleClose}
                onSaved={onBlockedTimeSaved}
                onDeleted={onBlockedTimeDeleted}
                hideActions
                onActionStateChange={setBlockedTimeActionState}
              />
            ) : (
              <>
                {submission.error && <Alert severity="error">{submission.error}</Alert>}
                <AppointmentPropertiesTable
                  mode="create"
                  showProvider={isOrgScheduleActive}
                  providerId={selectedProviderId}
                  onProviderSelect={handleProviderSelect}
                  patient={selectedPatient}
                  onPatientSelect={handlePatientSelect}
                  autoFocusPatientPicker={open && !initialPatient}
                  appointmentType={formsAndReminders.selectedAppointmentType}
                  appointmentTypeSuggested={isTypeSuggested}
                  onAppointmentTypeSelect={handleAppointmentTypeSelect}
                  date={durationSchedule.date}
                  startTime={durationSchedule.startTime}
                  duration={durationSchedule.selectedDuration}
                  onDateChange={durationSchedule.handleDateChange}
                  onStartTimeChange={durationSchedule.handleStartTimeChange}
                  onDurationChange={durationSchedule.handleDurationSelect}
                  recurrence={recurrence.recurrence}
                  onRecurrenceChange={handleRecurrenceChange}
                  onCustomRecurrenceClick={() => recurrence.setIsCustomRecurrenceModalOpen(true)}
                  telehealth={telehealth.telehealth}
                  onTelehealthChange={handleTelehealthChange}
                  modality={modality}
                  onModalityChange={handleModalityChange}
                  placeOfService={placeOfService}
                  onPlaceOfServiceChange={setPlaceOfService}
                  cptCodes={cptCodes}
                  onCptCodesChange={handleCptCodesChange}
                  useJotmeet={useJotmeet}
                  onUseJotmeetChange={setUseJotmeet}
                  sessionError={sessionError}
                  forms={formsAndReminders.selectedForms}
                  reminders={formsAndReminders.selectedReminders}
                  onFormsChange={handleFormsChange}
                  onRemindersChange={handleRemindersChange}
                  notes={notes}
                  onNotesChange={setNotes}
                  organizationId={organizationId}
                  labelIds={labelIds}
                  onLabelIdsChange={setLabelIds}
                />
              </>
            )}
          </Stack>
        </DialogContent>

        {activeMode === 'appointment' && (
          <DialogActions
            sx={{
              p: 2,
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 0,
              borderTop: `1px solid ${ELIG_TOKENS.line}`,
              bgcolor: 'background.paper',
              boxShadow: '0 -8px 24px rgba(28, 30, 133, 0.06)',
            }}
          >
            <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end' }}>
              <Button onClick={handleClose}>Cancel</Button>
              <Tooltip
                title={
                  !validation.isFormValid
                    ? validation.getValidationMessage()
                    : sessionError
                      ? sessionError
                      : validation.isFormValid && !submission.isSubmitting
                        ? 'Press Enter to create'
                        : ''
                }
                placement="top"
              >
                <span>
                  <Box sx={{ position: 'relative' }}>
                    <Button
                      variant="contained"
                      onClick={handleCreateEncounter}
                      disabled={
                        !validation.isFormValid || Boolean(sessionError) || submission.isSubmitting
                      }
                      sx={{
                        color: 'white',
                        paddingRight:
                          validation.isFormValid && !submission.isSubmitting ? '30px' : '16px',
                      }}
                    >
                      {submission.isSubmitting ? 'Saving...' : 'Save'}
                    </Button>
                    {validation.isFormValid && !submission.isSubmitting && (
                      <KeyboardReturnIcon
                        fontSize="small"
                        sx={{
                          position: 'absolute',
                          right: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          fontSize: '1rem',
                          opacity: 0.7,
                          color: 'white',
                        }}
                      />
                    )}
                  </Box>
                </span>
              </Tooltip>
            </Stack>
          </DialogActions>
        )}

        {activeMode === 'blocked_time' && (
          <DialogActions
            sx={{
              p: 2,
              alignItems: 'center',
              justifyContent: 'space-between',
              borderTop: `1px solid ${ELIG_TOKENS.line}`,
              bgcolor: 'background.paper',
              boxShadow: '0 -8px 24px rgba(28, 30, 133, 0.06)',
            }}
          >
            <Box>
              {blockedTimeActionState.isEditing && (
                <Button
                  color="error"
                  startIcon={<DeleteOutlineOutlinedIcon />}
                  onClick={() => blockedTimeFormRef.current?.remove()}
                  disabled={blockedTimeActionState.isSubmitting}
                >
                  Remove out of office
                </Button>
              )}
            </Box>
            <Stack direction="row" spacing={1.5}>
              <Button onClick={handleClose} disabled={blockedTimeActionState.isSubmitting}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => blockedTimeFormRef.current?.save()}
                disabled={blockedTimeActionState.isSubmitting}
              >
                {blockedTimeActionState.isSubmitting ? 'Saving...' : 'Save'}
              </Button>
            </Stack>
          </DialogActions>
        )}
      </Dialog>

      {/* Custom Recurrence Modal */}
      <CustomRecurrenceModal
        open={recurrence.isCustomRecurrenceModalOpen}
        onClose={() => recurrence.setIsCustomRecurrenceModalOpen(false)}
        onSave={recurrence.handleCustomRecurrenceSave}
        initialRule={recurrence.customRecurrenceRule || undefined}
      />
    </>
  );
};

export default AddAppointmentModal;
